import type { LearnTopic } from "../topic-types";

/**
 * Inner Child Work.
 *
 * Presented explicitly as a metaphor and reflective framework rather than a
 * literal clinical fact. People who find the language uncomfortable are
 * repeatedly invited to substitute their own or skip it entirely.
 */
export const INNER_CHILD_TOPIC: LearnTopic = {
  slug: "inner-child",
  emoji: "🧸",
  title: "Inner Child Work",
  tagline: "Meeting younger emotional states with care",
  theme: "lavender",
  category: "understanding-trauma",
  seoDescription:
    "A gentle, non-diagnostic introduction to inner child work: recognising younger emotional states, unmet needs and self-compassion. Educational guides, reflective prompts and a printable worksheet from Recovery Trauma™.",
  cardBlurb: "Recognise younger emotional states and practise self-compassion, gently.",
  isNew: true,
  intro: [
    "\"Inner child\" is a way of talking about the younger, more vulnerable emotional states that can still show up in adult life — a sudden wave of shame, a fear of being left, a longing to be looked after.",
    "This section explores that idea gently: what people mean by it, how to recognise it in yourself, and how to respond with more compassion than you may have received at the time.",
    "You do not need to believe in a literal inner child, or use this language at all, for the underlying idea to be useful: old feelings can resurface in new situations, and they often soften when they are met rather than argued with.",
  ],
  closing:
    "Inner child language is a metaphor and a reflective tool, not a clinical fact, a diagnosis or a required framework. If it does not fit you, there is no obligation to use it — the same ideas can be explored as \"younger emotional states\", \"old patterns\" or whatever words feel true to you. If this material stirs up more than feels manageable, a trauma-informed therapist can help you go at a safer pace.",
  continueTo: [
    { label: "Internal Family Systems (IFS)", to: "/learn/ifs" },
    { label: "Emotional Flashbacks", to: "/learn/emotional-flashbacks" },
    { label: "Inner Critic", to: "/learn/inner-critic" },
    { label: "Open your Journal", to: "/journal" },
  ],
  sections: [
    {
      slug: "understanding-inner-child-work",
      emoji: "📚",
      title: "Understanding inner child work",
      blurb: "What people mean by the phrase, and why it is a metaphor, not a diagnosis.",
      intro:
        "Plain-English education first. Read this at whatever pace feels comfortable — there is nothing to practise yet.",
      resources: [
        {
          slug: "what-people-mean-by-inner-child",
          kind: "learn",
          title: "What people mean by \"inner child\"",
          summary: "A metaphor for the younger emotional states that can still be active in you.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "When people talk about an \"inner child\", they usually mean the emotional states, beliefs and needs that formed early in life and can still be triggered now — even though the rest of you has grown up around them. It is a way of naming something many people already sense: that a reaction sometimes feels far younger than the situation calls for.",
            },
            {
              type: "note",
              text: "This is a metaphor and a reflective framework, not a literal clinical fact or a diagnosis. There is no smaller version of you living inside your body. If the phrase \"inner child\" does not sit well with you, you can use different language — \"younger part\", \"old pattern\", \"that old feeling\" — or skip this framework altogether. Nothing here depends on using this exact wording.",
            },
            {
              type: "list",
              title: "Some ideas people find useful",
              items: [
                "Emotional states from childhood can resurface in adult situations that echo old ones",
                "These states often carry needs that were not fully met at the time",
                "Recognising them is not regression — it is a way of understanding a reaction",
                "You are not \"stuck\" as a child; you are noticing that a feeling has old roots",
              ],
            },
          ],
        },
        {
          slug: "unmet-emotional-needs",
          kind: "learn",
          title: "Unmet emotional needs",
          summary: "Why a need that wasn't met at the time can still ask to be met now.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Every child has ordinary emotional needs: to be seen, comforted, believed, allowed to make mistakes, and to matter without having to earn it. When those needs go unmet often enough, the longing for them does not simply disappear — it can carry on quietly into adulthood, sometimes surfacing as a feeling that seems oddly large for the moment.",
            },
            {
              type: "list",
              title: "Common unmet needs people identify",
              items: [
                "To be comforted without having to stop feeling first",
                "To be believed, rather than corrected or dismissed",
                "To make mistakes without losing love or safety",
                "To be noticed without having to perform or achieve",
                "To have feelings without being told they were too much",
                "To be protected by someone bigger and steadier",
              ],
            },
            {
              type: "prose",
              text: "Noticing an unmet need is not about blaming anyone. Many caregivers were doing their best with what they had. It is simply useful information about what still asks for care now.",
            },
          ],
        },
        {
          slug: "recognising-younger-emotional-states",
          kind: "learn",
          title: "Recognising younger emotional states",
          summary: "Signs that an old feeling, rather than the present moment, has taken the wheel.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "A younger emotional state often has a distinct flavour — it can feel disproportionate, wordless, or oddly familiar, as if you have been here before at a different age.",
            },
            {
              type: "list",
              title: "Signs some people notice",
              items: [
                "A reaction that feels far bigger than the situation seems to warrant",
                "A sudden urge to hide, disappear, or be very good",
                "Feeling small, powerless, or much younger than your actual age",
                "A longing for someone to simply take care of things",
                "Shame that arrives instantly, before any thinking happens",
                "A physical sensation — a tight chest, a lump in the throat — that feels old",
              ],
            },
            {
              type: "note",
              text: "None of these signs are diagnostic on their own. They are simply patterns worth being curious about.",
            },
          ],
        },
        {
          slug: "self-compassion-as-a-skill",
          kind: "learn",
          title: "Self-compassion as a skill",
          summary: "Why being kind to yourself is something you can practise, not just feel.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Self-compassion is often mistaken for a personality trait — something you either have or don't. In practice it behaves more like a skill: a way of responding to your own pain that can be learned and strengthened over time, even by people who were never shown it.",
            },
            {
              type: "list",
              title: "What self-compassion tends to involve",
              items: [
                "Noticing pain without immediately minimising or arguing with it",
                "Speaking to yourself the way you might speak to someone you care about",
                "Remembering that struggling does not make you weak or broken",
                "Allowing comfort in, even briefly, rather than deflecting it",
              ],
            },
            {
              type: "quote",
              text: "You cannot go back and change how you were treated. You can change how you treat yourself now." ,
            },
          ],
        },
        {
          slug: "permission-to-have-needs",
          kind: "learn",
          title: "Giving yourself permission to have needs",
          summary: "Needs are not a character flaw, however early you learned to hide them.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "Many people learned early that having needs was inconvenient, dangerous or embarrassing, and got very good at not having any — at least on the surface. Needs did not stop existing; they simply stopped being spoken.",
            },
            {
              type: "list",
              title: "Reminders worth returning to",
              items: [
                "Having needs is part of being human, not a personal failing",
                "A need you were taught to hide is still allowed to exist now",
                "You are allowed to want comfort, rest, reassurance or help",
                "Meeting your own needs sometimes does not mean no one else ever will",
              ],
            },
            {
              type: "note",
              text: "If naming a need feels frightening or shameful, that reaction itself is worth being gentle with — it likely made sense once.",
            },
          ],
        },
      ],
    },
    {
      slug: "noticing-and-responding",
      emoji: "🔎",
      title: "Noticing and responding",
      blurb: "Gentle reflective exercises for meeting a younger emotional state.",
      intro:
        "These are invitations, not assignments. You can stop at any point, come back to it another day, or not do it at all.",
      resources: [
        {
          slug: "meeting-a-younger-emotional-state",
          kind: "reflect",
          title: "Meeting a younger emotional state",
          summary: "Slowing down around a strong or disproportionate reaction.",
          minutes: 8,
          blocks: [
            {
              type: "prose",
              text: "Bring to mind a recent moment when a reaction felt bigger, older or more familiar than the situation called for. There is no need to relive it in detail — a light touch is enough.",
            },
            {
              type: "prompts",
              title: "Reflection prompts",
              items: [
                "What was the feeling, in one or two words?",
                "If that feeling had an age, roughly how old might it feel?",
                "What might that younger state have needed in that moment?",
                "Is there something you could offer it now, even briefly?",
                "What tone would you use if you were speaking to a child who felt this way?",
              ],
            },
            {
              type: "note",
              text: "If this exercise brings up more than feels manageable, it is completely fine to stop and return to something grounding instead — the Grounding tools or a slow breath are always available.",
            },
          ],
        },
        {
          slug: "identifying-what-was-needed",
          kind: "reflect",
          title: "Identifying what was needed",
          summary: "Naming the specific care that was missing at the time.",
          minutes: 7,
          blocks: [
            {
              type: "prompts",
              items: [
                "What did I need to hear, that I didn't hear, at that age?",
                "What did I need someone to do, that no one did?",
                "Who, if anyone, might have been able to offer that?",
                "How might things feel different if that need had been met?",
                "What is one small way I could offer myself something similar today?",
              ],
            },
            {
              type: "prose",
              text: "You are not trying to rewrite the past. You are simply naming what was missing, so that the longing for it makes more sense.",
            },
          ],
        },
        {
          slug: "a-letter-to-your-younger-self",
          kind: "reflect",
          title: "A letter to your younger self",
          summary: "An optional writing exercise — never required.",
          minutes: 10,
          blocks: [
            {
              type: "prose",
              text: "Some people find it useful to write a short letter to themselves at a younger age. Others find this uncomfortable or not for them. Either response is completely fine — you can adapt the exercise, write to \"a part of me\" instead, or skip it entirely.",
            },
            {
              type: "steps",
              title: "If you would like to try it",
              items: [
                { label: "Choose an age", note: "Whatever feels relevant, or leave it unspecified." },
                { label: "Write what you noticed", note: "What that time seemed to be like for them." },
                { label: "Write what you wish they had heard", note: "In plain, warm words." },
                { label: "Sign it from you, now", note: "As the person who made it through." },
              ],
            },
            {
              type: "note",
              text: "There is no correct length or tone. A few honest sentences count as much as a full page.",
            },
          ],
        },
        {
          slug: "self-compassion-practice",
          kind: "practice",
          title: "A short self-compassion practice",
          summary: "A brief, optional way to respond to a difficult moment kindly.",
          minutes: 5,
          blocks: [
            {
              type: "steps",
              items: [
                { label: "Notice", note: "Name what is happening: \"this is a hard moment.\"" },
                { label: "Normalise", note: "Remember that struggling is part of being human, not a flaw." },
                { label: "Offer warmth", note: "A hand on your chest, a slower breath, or kind words in your own voice." },
                { label: "Ask what would help", note: "Even a small answer is enough — rest, water, a pause." },
              ],
            },
            {
              type: "note",
              text: "This is a gentle practice, not a fix. It is fine if it feels awkward at first — that usually fades with repetition, not effort.",
            },
          ],
        },
      ],
    },
    {
      slug: "exercises-and-worksheets",
      emoji: "📄",
      title: "Exercises & worksheets",
      blurb: "A printable page for reflection away from a screen.",
      intro: "Print this, or copy the headings into your own journal.",
      resources: [
        {
          slug: "inner-child-reflection-worksheet",
          kind: "worksheet",
          title: "Meeting a Younger Emotional State",
          summary: "A structured page for exploring one moment, need and response.",
          worksheetFields: [
            { label: "What happened, in a sentence or two", lines: 3 },
            { label: "The feeling that showed up", lines: 2 },
            { label: "How old that feeling seemed to be", lines: 1 },
            { label: "What that younger state might have needed", lines: 3 },
            { label: "Who, if anyone, could have offered that at the time", lines: 2 },
            { label: "What I can offer myself now, even in a small way", lines: 3 },
            { label: "A kind sentence I could say to myself next time this shows up", lines: 2 },
          ],
        },
      ],
    },
  ],
};
